import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import {Grid, Button} from "@material-ui/core";
import moment from "moment/moment";
import Card from "@material-ui/core/Card";
import React, {useEffect, useState} from 'react'
import {useQuery, useLazyQuery} from "@apollo/client";
import gql from "graphql-tag";
import {useStyles} from "../pages/Dashboard";
import {NavLink} from "react-router-dom";
import {CellPill} from "./StatusPill";
import CalendarHeatmap from "react-calendar-heatmap";

import 'react-calendar-heatmap/dist/styles.css';
import {DateTime} from "luxon";
import {Loading} from "./LoadingFork";
import {tableStyles} from "./BookingSummary";

function getSvgPadding(value) {
    const padding =  value < 10 ? 8 : value < 100 ? 7 : 6
    return padding;
}

export function BookingSummaryV2({installation}) {
    const [date, setDate] = useState(DateTime.now().setZone(installation.timeZoneName).toFormat('YYYY-MM-DD'));
    const [firstDay, setFirstDay] = useState(DateTime.now().setZone(installation.timeZoneName).startOf('month'));
    const classes = useStyles();
    const tableClasses = tableStyles();

    // Light query: only date + count for the heatmap
    const {data: monthlyBookings, loading: loadingMonthlyBookings, refetch} = useQuery(gql`
        query MonthlyBookings($installationId:ID!, $firstDay:String)
        {
            getMonthlyBookings(installationId:$installationId, firstDay:$firstDay){
                date
                count
            }
        }
    `, {
        variables: {
            installationId: installation.id,
            firstDay: firstDay.toFormat('yyyy-MM-dd'),
        },
        fetchPolicy: "no-cache",
    });

    // On-demand query: fetch bookings only when a date is clicked
    const [fetchBookingsForDate, {data: dayBookingsData, loading: loadingDayBookings}] = useLazyQuery(gql`
        query BookingsByDate($installationId:ID!, $date:String!)
        {
            getBookingsByDate(installationId:$installationId, date:$date){
                plate
                startDay
                endDay
                id
                rateCard {
                    paymentStripe
                }
            }
        }
    `, {
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        refetch({
            installationId: installation.id,
            firstDay: firstDay.toFormat('yyyy-MM-dd'),
        }).catch(e => console.error("Error fetching bookings:", e));
    }, [firstDay, installation.id, refetch]);

    if (loadingMonthlyBookings) return <Loading />;

    let monthlyBookingsData = monthlyBookings?.getMonthlyBookings

    if (!monthlyBookingsData) return <Loading />;

    if (monthlyBookingsData.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6">
                        No bookings found for this month.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const handleMonthChange = (action) => {
        const newFirstDay = action === 'next'
          ? firstDay.plus({ month: 1 }).startOf('month')
          : firstDay.minus({ month: 1 }).startOf('month');

        if (!newFirstDay.hasSame(firstDay, 'month')) {
            setFirstDay(newFirstDay);
        }
    };

    const handleDateClick = (day) => {
        const formatted = moment(day).format('YYYY-MM-DD');
        setDate(formatted);
        fetchBookingsForDate({
            variables: {
                installationId: installation.id,
                date: formatted,
            }
        });
    }

    const getCountForDay = (day) => {
        const dayData = monthlyBookings?.getMonthlyBookings?.find(d => d.date === day);
        return dayData?.count ?? 0;
    }

    const getBookingType = (booking) => booking.rateCard?.paymentStripe ? 'Prepaid' : 'Chargeback';

    const escapeCSVCell = (value) => {
        let cell = String(value).replace(/"/g, '""');
        if (/^[=+\-@]/.test(cell)) cell = "'" + cell;
        return `"${cell}"`;
    }

    const exportBookingsCSV = (day, bookings) => {
        const header = ['Plate', 'Start', 'End', 'Type'];
        const rows = bookings.map(b => {
            const start = DateTime.fromISO(new Date(b.startDay).toISOString(), {locale: "en-AU"})
                .setZone(installation.timeZoneName).toFormat("d MMM yyyy HH:mm ZZZZ");
            const end = DateTime.fromISO(new Date(b.endDay).toISOString(), {locale: "en-AU"})
                .setZone(installation.timeZoneName).toFormat("d MMM yyyy HH:mm ZZZZ");
            return [b.plate.toUpperCase(), start, end, getBookingType(b)];
        });
        const csv = [header, ...rows].map(row => row.map(cell => escapeCSVCell(cell)).join(',')).join('\n');
        const blob = new Blob([csv], {type: 'text/csv'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookings_${day}.csv`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 0);
    }

    const getDayComponent = (day) => {
        const count = getCountForDay(day);
        const bookings = dayBookingsData?.getBookingsByDate || [];

        return <>
            <p>
                <b>Date: </b>
                <span>{day}</span>
            </p>
            <p>
                <b>Total Bookings: </b>
                <span>{count}</span>
                {!loadingDayBookings && bookings.length > 0 && (
                    <Button
                        variant="contained"
                        size="small"
                        style={{ marginLeft: 16, backgroundColor: '#37B534', color: '#fff' }}
                        onClick={() => exportBookingsCSV(day, bookings)}
                    >
                        Export CSV
                    </Button>
                )}
            </p>
            {loadingDayBookings && <Loading />}
            {!loadingDayBookings && bookings.length > 0 &&
            <div style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                <thead>
                <tr>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Plate</th>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Start</th>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>End</th>
                    <th style={{ padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Type</th>
                </tr>
                </thead>
                <tbody>
                {bookings.map(b => {
                    function stringToZonedString(dateString, timeZone){
                        return DateTime
                            .fromISO(new Date(dateString).toISOString(), {locale: "en-AU"})
                            .setZone(timeZone).toFormat("d MMM yyyy HH:mm ZZZZ")
                    }
                    const type = getBookingType(b);
                    return <tr key={b.id}>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}><NavLink to={`/${installation.brandingKey}/bookings/${b.id}`}>
                            <CellPill color='#37B534' width='105px' size='small'>{b.plate.toUpperCase()}</CellPill>
                        </NavLink></td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{stringToZonedString(b.startDay, installation.timeZoneName)}</td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{stringToZonedString(b.endDay, installation.timeZoneName)}</td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}><CellPill color={type === 'Prepaid' ? '#2196F3' : '#FF9800'} width='auto' size='small'>{type}</CellPill></td>
                    </tr>
                })}
                </tbody>
            </table>
            </div>}
        </>
    }


    return (
        <Card className={classes.card} style={{display: 'flex', minHeight: 600, justifyContent: "center", alignItems: "center"}}>
            <CardContent className={tableClasses.cardContentRoot}>
                <Typography variant="subtitle2" style={{fontSize: '30px', textAlign: 'center', fontWeight: 700}}>
                    Bookings Summary
                </Typography>
                <Typography variant="subtitle2" style={{fontSize: '25px', textAlign: 'center', fontWeight: 500, marginBottom: '1rem' }}>
                    <span style={{ cursor: 'pointer', marginRight: 16 }} onClick={() => handleMonthChange('prev')}>
                        ←
                    </span>
                    {firstDay.toFormat('LLLL, yyyy')}
                    <span style={{ cursor: 'pointer', marginLeft: 16 }} onClick={() => handleMonthChange('next')}>
                        →
                    </span>
                </Typography>
                <Grid container spacing={1} className={tableClasses.root}>
                    <Grid item xs={12}>
                        <CalendarHeatmap
                            startDate={DateTime.fromJSDate(new Date(monthlyBookingsData[0]?.date)).minus({ 'day': 1 })}
                            endDate={DateTime.fromJSDate(new Date(monthlyBookingsData[monthlyBookingsData.length - 1]?.date)).minus({ 'day': 1 })}
                            values={monthlyBookingsData}
                            horizontal={false}
                            showWeekdayLabels={false}
                            showMonthLabels={false}
                            titleForValue={(value) => {
                                return (
                                    <>
                                        <p>
                                            <b>Date: </b>
                                            <span>{value?.date}</span>
                                        </p>

                                        <p>
                                            <b>, Bookings: </b>
                                            <span>{value?.count}</span>
                                        </p>
                                    </>

                                )
                            }}
                            classForValue={(value) => {
                                if (!value) {
                                    return "color-empty";
                                }
                                return value.count > 4 ? 'color-github-4' : `color-github-${value.count}`;
                            }}
                            onClick={(value) => {
                                handleDateClick(value.date)
                            }}
                            transformDayElement={(element, value) => {
                                if (!value) return <></>;

                                return (
                                <g style={{cursor: "pointer"}} onClick={() => handleDateClick(value.date)}>
                                    {element}
                                    <text
                                        x={element["props"].x + 1}
                                        y={element["props"].y + 3}
                                        style={{
                                            fontSize: "2px",
                                            fill: "#000",
                                            textAlign: "center"
                                        }}
                                    >
                                        {moment(value.date).format('D MMM')}
                                    </text>
                                    <text
                                        x={element["props"].x + getSvgPadding(value.count)}
                                        y={element["props"].y + 8}
                                        style={{
                                            fontSize: "2px",
                                            fill: "#000",
                                            textAlign: "center"
                                        }}
                                    >
                                        {value.count}
                                    </text>
                                </g>
                            )}}
                        />
                    </Grid>
                    <Grid item xs={12} style={{paddingTop: "20px", paddingBottom: '40px'}}>
                        {getDayComponent(date)}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
